import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { Button, CircularProgress } from "@mui/material";
import { useRef, useState } from "react";
import { FaPaperPlane } from "react-icons/fa";
import MentionTextarea from "components/Input/MentionTextarea";
import CommentAttachments from "./CommentAttachments";
import { useAttachmentUploads } from "hooks/useAttachmentUploads";
import { handleAttachmentPaste } from "utils/attachmentClipboard";

export const MAX_COMMENT_ATTACHMENTS = 10;

// Compact by default; Expand gives a long comment room to be written and read back.
const BOX_SIZES = {
  compact: { rows: 4, maxRows: 14 },
  expanded: { rows: 12, maxRows: 28 },
};

const CommentComposer = ({
  text,
  onTextChange,
  mentionedUserIds,
  onMentionedUserIdsChange,
  users,
  attachments = [],
  setAttachments,
  onSubmit,
  disabled = false,
}) => {
  const fileInputRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const size = expanded ? BOX_SIZES.expanded : BOX_SIZES.compact;
  const { isUploading, uploadFiles, addLinks } = useAttachmentUploads({
    max: MAX_COMMENT_ATTACHMENTS,
    noun: "comment",
  });

  const appendAttachments = (added) => setAttachments((previous) => [...previous, ...added]);

  const handleFilesSelected = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    uploadFiles(files, attachments, appendAttachments);
  };

  const handlePaste = (event) => {
    if (disabled || isUploading) return;
    handleAttachmentPaste(event, {
      onFiles: (files) => uploadFiles(files, attachments, appendAttachments),
      onLinks: (links) => addLinks(links, attachments, appendAttachments),
    });
  };

  const removeAttachment = (index) => {
    setAttachments((previous) => previous.filter((_, i) => i !== index));
  };

  const canSend = !disabled && !isUploading && (text.trim().length > 0 || attachments.length > 0);

  return (
    <div className="mt-3" onPaste={handlePaste}>
      <MentionTextarea
        value={text}
        onChange={onTextChange}
        mentionedUserIds={mentionedUserIds}
        onMentionedUserIdsChange={onMentionedUserIdsChange}
        users={users}
        rows={size.rows}
        maxRows={size.maxRows}
        disabled={disabled}
        placeholder="Add a comment... Type @ to mention someone, or paste files to attach them"
      />

      <CommentAttachments attachments={attachments} onRemove={removeAttachment} />

      <div className="mt-2 flex items-center justify-between gap-2">
        <Button
          size="small"
          color="inherit"
          startIcon={
            isUploading ? (
              <CircularProgress size={14} />
            ) : (
              <AttachFileOutlinedIcon fontSize="small" />
            )
          }
          disabled={disabled || isUploading}
          onClick={() => fileInputRef.current?.click()}
          sx={{ textTransform: "none" }}
        >
          {isUploading ? "Uploading..." : "Attach"}
        </Button>
        <Button
          size="small"
          color="inherit"
          startIcon={
            expanded ? (
              <CloseFullscreenIcon fontSize="small" />
            ) : (
              <OpenInFullIcon fontSize="small" />
            )
          }
          onClick={() => setExpanded((current) => !current)}
          aria-pressed={expanded}
          sx={{ textTransform: "none", mr: "auto" }}
        >
          {expanded ? "Collapse" : "Expand"}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFilesSelected}
        />
        <Button
          size="small"
          variant="contained"
          startIcon={<FaPaperPlane />}
          disabled={!canSend}
          onClick={() => {
            onSubmit();
            setExpanded(false);
          }}
        >
          Send
        </Button>
      </div>
    </div>
  );
};

export default CommentComposer;
